
  import {computed, defineComponent, onMounted, reactive, toRefs, watch} from "vue";
  import {t} from "@/mixin/mixins";
  import store from "@/store/store";
  import {HeaderGetters} from "@/store/modules/header";
  import {EmployeeStatisticPerformance} from "@/model/EmployeeStatisticPerformance";
  import eventBus from "@/eventbus";
  import {MessageTypes} from "@/components/message-types";
  import StatisticService, {OperatorPerformanceDto} from "@/service/StatisticService";
  import router from "@/router";
  import moment from "moment/moment";

  export default defineComponent({
    name: "StatisticPerformance",
    setup() {
      const initialState = {
        yearMonth: null as unknown as string,
        performances: new Array<EmployeeStatisticPerformance>()
      };
      const state = reactive({...initialState});

      const headers = [
        {
          text: t("statistic.performance.table.header.employeeId"),
          value: "employeeId"
        },
        {
          text: t("statistic.performance.table.header.employeeName"),
          value: "employeeName"
        },
        {
          text: t("statistic.performance.table.header.companyLocation"),
          value: "companyLocation"
        },
        {
          text: t("statistic.performance.table.header.workingMinutes"),
          value: "workingMinutes"
        },
        {
          text: t("statistic.performance.table.header.positions"),
          value: "positions"
        },
        {
          text: t("statistic.performance.table.header.positionsPerHour"),
          value: "positionsPerHour"
        },
        {
          text: t("statistic.performance.table.header.standardDeviation"),
          value: "standardDeviation"
        },
        {
          text: t("statistic.performance.table.header.median"),
          value: "median"
        }
      ];

      onMounted(() => {
        state.yearMonth = moment().format("YYYY-MM");
      });

      const selectedMonth = computed(() => store.getters["header/" + HeaderGetters.GET_MONTH]);

      watch(() => selectedMonth, () => {
        state.yearMonth = selectedMonth.value;
      }, {deep: true});

      watch(() => state.yearMonth, () => {
        loadStatisticPerformances();
      }, {deep: true});

      const loadStatisticPerformances = () => {
        if (state.yearMonth) {
          StatisticService.getOperatorPerformances(state.yearMonth)
            .then(operators => {
              state.performances = operators.map(toRow);
            })
            .catch(() => {
              eventBus.$emit(MessageTypes.ERROR, t("errors.generalError"));
            });
        }
      };

      const toRow = (operator: OperatorPerformanceDto): EmployeeStatisticPerformance => ({
        employeeUuid: operator.employee.id,
        employeeId: operator.employee.employeeNumber,
        employeeName: `${operator.employee.firstName} ${operator.employee.lastName}`,
        companyLocation: operator.employee.companyLocation,
        workingMinutes: operator.total.workedMinutes,
        positions: operator.total.positions,
        positionsPerHour: Math.round(operator.total.positionsPerHour ?? 0),
        standardDeviation: Math.round((operator.positionsPerHourStandardDeviation ?? 0) * 100) / 100,
        median: Number.parseFloat((operator.medianPositionsPerHour ?? 0).toFixed(2))
      });

      const toTimeFormat = (minutes: number) => {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        const hour = h < 10 ? "0" + h : h;
        const minute = m < 10 ? "0" + m : m;
        return hour + ":" + minute;
      };

      const openCountingPerformance = (employeeUuid: string) => {
        router.push({
          path: "inventories",
          query: {
            tab: "1",
            employeeUuid: employeeUuid
          }
        });
      };

      return {
        ...toRefs(state),
        headers,
        toTimeFormat,
        openCountingPerformance
      };
    }
  });
